import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getUser, canManageUsers, canViewBilling, canViewOversight, canViewStrategic, isPlatformAdmin, clearAuth } from '@/utils/auth';
import { trackEvent } from '@/utils/telemetry';
import { LayoutDashboard, FolderKanban, ClipboardList, CheckSquare, BarChart3, Users, User, FileText, CreditCard, Shield, LogOut, HelpCircle, Gift, MessageSquare, Award, Send, Sparkles, PenLine, UserPlus, BookOpen, Scale, FileCheck, Brain, ShieldCheck, CalendarDays, Radio, Activity, Settings, ShoppingCart, IdCard, Search, Library, Network, Building2 } from 'lucide-react';
import { SheetContent } from '@/components/ui/sheet';
import MenuLongPress from './MenuLongPress';
import ReviewPrompt from './ReviewPrompt';
import FeedbackWidget from './FeedbackWidget';
import CreditBalance from './CreditBalance';
import NotificationBell from './NotificationBell';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { TrialBanner } from './TrialBanner';
import UserAccountMenu from './UserAccountMenu';
import { useBranding } from '@/contexts/BrandingContext';
import { initSecurityMonitor } from '@/utils/securityMonitor';
import api from '@/utils/api';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileNavQuery, setMobileNavQuery] = useState('');
  const [secCount, setSecCount] = useState(0);
  const { brandName, logoUrl } = useBranding();

  // Reverse-engineering / tamper monitor (authenticated sessions only).
  useEffect(() => {
    if (user) initSecurityMonitor();
  }, [user]);

  // Admin security badge: poll the lightweight summary for new incidents.
  useEffect(() => {
    const isAdmin = user && ['HQ', 'ADMIN'].includes(user.role);
    if (!isAdmin) return;
    let active = true;
    const fetchSummary = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const r = await api.get('/admin/security/summary');
        if (active) setSecCount(r.data?.total || 0);
      } catch (_) { /* ignore */ }
    };
    fetchSummary();
    const id = setInterval(fetchSummary, 60000);
    return () => { active = false; clearInterval(id); };
  }, [user]);

  // Telemetry: record every protected route visit (used by weekly usage audit)
  useEffect(() => {
    if (user && location.pathname) {
      trackEvent('page_view', location.pathname);
    }
  }, [location.pathname, user]);

  const handleLogout = () => {
    // Use unified auth utility to clear all auth data
    clearAuth();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { path: '/signal-check', label: 'Signal Check', icon: Radio, show: true },
    { path: '/signal-dashboard', label: 'Alignment Index', icon: Activity, show: canViewOversight() },
    { path: '/methodology', label: 'Methodology', icon: BookOpen, show: true },
    { path: '/onboarding', label: 'Setup Wizard', icon: Sparkles, show: isPlatformAdmin() },
    { path: '/projects', label: 'Company', icon: FolderKanban, show: canViewOversight() },
    { path: '/forms', label: 'Forms', icon: ClipboardList, show: true },
    { path: '/tasks', label: 'Tasks', icon: CheckSquare, show: canViewOversight() },
    { path: '/signatures', label: 'Signatures', icon: PenLine, show: canViewStrategic() },
    { path: '/teams', label: 'Teams', icon: UserPlus, show: isPlatformAdmin() },
    { path: '/workers-voice', label: 'Workers Voice', icon: MessageSquare, show: user?.role === 'WORKER' },
    { path: '/surveys', label: 'Surveys', icon: MessageSquare, show: canViewOversight() },
    { path: '/instructions', label: 'Instructions', icon: Send, show: canViewOversight() },
    { path: '/leadership', label: 'Leadership', icon: Award, show: canViewStrategic() },
    { path: '/odsi', label: 'ODSI', icon: BarChart3, show: canViewOversight() },
    { path: '/iso45001', label: 'ISO 45001', icon: Shield, show: canViewOversight() },
    { path: '/iso45001/audit', label: 'Audit Checklist', icon: ClipboardList, show: canViewStrategic() },
    { path: '/compliance-library', label: 'OSH Library', icon: Library, show: true },
    { path: '/intelligence', label: 'Intelligence', icon: FileText, show: canViewStrategic() },
    { path: '/users', label: 'Users', icon: Users, show: canManageUsers() },
    { path: '/reports', label: 'Reports', icon: FileText, show: canViewStrategic() },
    { path: '/merit-demerit', label: 'Merit/Demerit', icon: Scale, show: canViewStrategic() },
    { path: '/document-compliance', label: 'Doc Compliance', icon: FileCheck, show: canViewOversight() },
    { path: '/executive-reports', label: 'Executive Intel', icon: Brain, show: canViewStrategic() },
    { path: '/governance', label: 'Governance', icon: Shield, show: canViewStrategic() },
    { path: '/compliance-dashboard', label: 'Compliance HQ', icon: ShieldCheck, show: canViewStrategic() },
    { path: '/itot-convergence', label: 'IT/OT Convergence', icon: Network, show: isPlatformAdmin() },
    { path: '/org-structure', label: 'Org Structure', icon: Building2, show: isPlatformAdmin() },
    { path: '/compliance-calendar', label: 'Expiry Calendar', icon: CalendarDays, show: canViewStrategic() },
    { path: '/billing', label: 'Billing', icon: CreditCard, show: canViewBilling() },
    { path: '/marketplace', label: 'Marketplace', icon: ShoppingCart, show: true },
    { path: '/admin/freelancers', label: 'Associate Consultants', icon: IdCard, show: isPlatformAdmin() },
    { path: '/settings', label: 'Settings', icon: Settings, show: isPlatformAdmin() },
    { path: '/referrals', label: 'Referrals', icon: Gift, show: true },
    { path: '/profile', label: 'Profile', icon: User, show: true },
    { path: '/guide', label: 'User Guide', icon: BookOpen, show: true },
    { path: '/help', label: 'Help', icon: HelpCircle, show: true },
  ].filter(item => item.show);

  const mobileNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/signal-check', label: 'Signal', icon: Radio },
    { path: '/workers-voice', label: 'Voice', icon: MessageSquare },
    { path: '/forms', label: 'Forms', icon: ClipboardList },
    { path: '/profile', label: 'Profile', icon: User },
  ];

  const filteredMobileNavItems = useMemo(() => {
    const q = mobileNavQuery.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [navItems, mobileNavQuery]);

  return (
    <>
    <div className="min-h-screen bg-background">
      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Trial Status Banner (global) */}
      <TrialBanner />

      {/* Desktop Header — sticky with z-[10000] so the user menu (logout) is
          always reachable even when full-viewport overlays like
          WhatsNewSplash (z-9999) or modal dialogs are open. */}
      <header className="hidden md:flex items-center justify-between p-4 border-b border-border bg-card sticky top-0 z-[10000]">
        <div className="flex items-center gap-4">
          <img src={logoUrl || "/safer-i-logo.png"} alt={brandName} className="w-10 h-10 rounded-lg object-contain" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">{brandName}</h1>
            <p className="text-xs text-muted-foreground">{user?.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <CreditBalance />
          <NotificationBell />
          <UserAccountMenu />
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <img src={logoUrl || "/safer-i-logo.png"} alt={brandName} className="w-8 h-8 rounded-lg object-contain" />
          <h1 className="text-lg font-bold">{brandName}</h1>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <MenuLongPress open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="right" className="w-[280px] p-0 flex flex-col">
            {/* Sticky top: user info + logout + search — always visible */}
            <div className="px-6 pt-6 pb-3 border-b bg-card sticky top-0 z-10">
              <div className="mb-3 pr-8">
                <p className="font-semibold truncate">{user?.display_name || user?.full_name}</p>
                <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-1 uppercase">{user?.role}</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                data-testid="mobile-logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
              <div className="relative mt-3">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="search"
                  inputMode="search"
                  autoComplete="off"
                  placeholder="Search menu…"
                  value={mobileNavQuery}
                  onChange={(e) => setMobileNavQuery(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="mobile-nav-search"
                />
              </div>
            </div>

            {/* Scrollable nav */}
            <nav className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-1 pb-24" data-testid="mobile-nav-list">
              {filteredMobileNavItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6" data-testid="mobile-nav-empty">
                  No matches for &ldquo;{mobileNavQuery}&rdquo;
                </p>
              ) : (
                filteredMobileNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => { setMobileMenuOpen(false); setMobileNavQuery(''); }}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                      location.pathname === item.path
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`menu-link-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                ))
              )}
            </nav>
          </SheetContent>
        </MenuLongPress>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex md:flex-col w-64 border-r border-border bg-card min-h-[calc(100vh-73px)]">
          <nav className="p-4 space-y-1 flex-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 ${
                  location.pathname === item.path
                    ? 'bg-primary text-primary-foreground scale-95'
                    : 'hover:bg-muted hover:-translate-y-0.5'
                }`}
                data-testid={`sidebar-link-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {item.path === '/settings' && secCount > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center"
                    data-testid="nav-security-badge"
                    title={`${secCount} security item${secCount !== 1 ? 's' : ''} need attention`}
                  >
                    {secCount > 99 ? '99+' : secCount}
                  </span>
                )}
              </Link>
            ))}
          </nav>
          {/* Sticky logout footer — visible to ALL roles */}
          <div className="border-t p-3">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-rose-600 hover:bg-rose-50 hover:text-rose-700 transition-all"
              data-testid="sidebar-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 page-container">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav md:hidden">
        <div className="flex justify-around items-center h-16 px-2">
          {mobileNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-md touch-target transition-all duration-200 ${
                  isActive ? 'text-primary scale-95' : 'text-muted-foreground'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'stroke-[2]' : 'stroke-[1.5]'}`} />
                <span className={`text-xs ${isActive ? 'font-semibold' : 'font-normal'}`}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
    
    {/* Review Prompt Modal */}
    <ReviewPrompt />
    
    {/* Floating Feedback Widget */}
    <FeedbackWidget />
    </>
  );
}